import React, { useCallback, useEffect, useState } from "react";
import {
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	Chip,
	Collapse,
	IconButton,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import EditIcon from "@mui/icons-material/Edit";
import Groups2Icon from "@mui/icons-material/Groups2";
import HistoryIcon from "@mui/icons-material/History";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import PersonIcon from "@mui/icons-material/Person";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ShareIcon from "@mui/icons-material/Share";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { EventoComidaDialog } from "../calendario/EventoComidaDialog";
import { FormAsistirActividad } from "../actividades/FormAsistirActividad";
import { AnadirGastoDialog } from "../contabilidad/AnadirGasto.Dialog";
import { RegistroAccionesComidaDialog } from "../calendario/RegistroAccionesComidaDialog";
import { InvitacionActividad } from "../calendario/InvitacionActividad";
import { getDatosContables } from "../../services/apis";
import { formatoHorarioEvento, getTextoEncargadosEvento } from "./eventosUtils";

export const EventoLimpiezaCard = ({ evento, opTrabajadores, onChange }) => {
	const encargados = getTextoEncargadosEvento(evento, opTrabajadores);
	const [desplegar, setDesplegar] = useState(false);
	const [editar, setEditar] = useState(false);
	const [asistir, setAsistir] = useState(false);
	const [gastoOpen, setGastoOpen] = useState(false);
	const [dialogRegistros, setDialogRegistros] = useState(false);
	const [invitar, setInvitar] = useState(false);
	const [anioSeleccionado] = useState(new Date().getFullYear());
	const [currenApartado] = useState(evento.id_apartado || null);
	const [bloqueado] = useState(evento.cerrado || 0);

	const totalAsistentes = (evento.asistentes || []).filter((asistente) => Number(asistente.miembro_asiste ?? 1) === 1).length;

	const cargarDatos = useCallback(async () => {
		await getDatosContables(anioSeleccionado);
	}, [anioSeleccionado]);

	useEffect(() => {
		if (gastoOpen) {
			cargarDatos();
		}
	}, [gastoOpen, cargarDatos]);

	const handleAdd = () => {
		setGastoOpen(false);
	};

	return (
		<Card className={`cleaning-card ${bloqueado === 1 ? "cleaning-card-closed" : ""}`}>
			<CardContent className="cleaning-card-content">
				<Box className="cleaning-card-top">
					<Box className="cleaning-card-icon">
						<CleaningServicesIcon />
					</Box>
					<Box className="cleaning-card-title-block">
						<Typography variant="h6" className="cleaning-card-title" title={evento.asunto || evento.descripcion}>
							{evento.asunto || evento.descripcion || "Neteja"}
						</Typography>
						<Stack direction="row" className="cleaning-card-meta">
							<span className="events-cleaning-date-alert">
								<AccessTimeIcon fontSize="inherit" />
								{formatoHorarioEvento(evento)}
								<WarningAmberIcon fontSize="inherit" />
							</span>
						</Stack>
					</Box>
					<IconButton className="cleaning-history-button" size="small" onClick={() => setDialogRegistros(true)}>
						<HistoryIcon fontSize="small" />
					</IconButton>
				</Box>

				<Box className="cleaning-owner-row">
					<PersonIcon fontSize="small" />
					<Typography className="cleaning-owner">
						{encargados.label}: {encargados.texto}
					</Typography>
					<Chip
						size="small"
						className={bloqueado === 1 ? "cleaning-status-chip cleaning-status-closed" : "cleaning-status-chip cleaning-status-open"}
						label={bloqueado === 1 ? "Tancada" : "Oberta"}
					/>
				</Box>

				<Button
					fullWidth
					variant="outlined"
					className="cleaning-toggle"
					endIcon={desplegar ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
					onClick={() => setDesplegar(!desplegar)}
				>
					<Groups2Icon fontSize="small" />
					{desplegar ? "Ocultar assistents" : `Veure assistents (${totalAsistentes})`}
				</Button>

				<Collapse in={desplegar} timeout="auto" unmountOnExit>
					<TableContainer component={Paper} elevation={0} className="cleaning-table-wrap">
						<Table size="small" className="cleaning-table">
							<TableHead>
								<TableRow>
									<TableCell>Assistent</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{(evento.asistentes || []).map((asistente) => (
									<TableRow key={asistente.id_usuario}>
										<TableCell>{asistente.nombre}</TableCell>
									</TableRow>
								))}
								{(evento.asistentes || []).length === 0 && (
									<TableRow>
										<TableCell align="center">No hi ha assistents</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>
				</Collapse>
			</CardContent>

			<CardActions className="cleaning-actions">
				<Button size="small" variant="contained" startIcon={<ShareIcon />} disabled={bloqueado === 1} onClick={() => setInvitar(true)}>
					Convidar
				</Button>
				<Button size="small" variant="contained" startIcon={<HowToRegIcon />} disabled={bloqueado === 1} onClick={() => setAsistir(true)}>
					Assistir
				</Button>
				<Button size="small" variant="outlined" startIcon={<EditIcon />} disabled={evento.opciones !== 1 || bloqueado === 1} onClick={() => setEditar(true)}>
					Editar
				</Button>
				<Button size="small" variant="outlined" startIcon={<ReceiptLongIcon />} disabled={bloqueado === 1} onClick={() => setGastoOpen(true)}>
					Despesa
				</Button>
			</CardActions>

			{editar && (
				<EventoComidaDialog
					open={editar}
					setOpen={setEditar}
					tarea={evento}
					opTrabajadores={opTrabajadores}
					setSuccesCrearComida={onChange}
					permiteInvitados={false}
				/>
			)}
			{asistir && (
				<FormAsistirActividad
					open={asistir}
					setOpen={setAsistir}
					tarea={evento}
					setSuccesCrearComida={onChange}
					permiteInvitados={false}
				/>
			)}
			{dialogRegistros && (
				<RegistroAccionesComidaDialog
					open={dialogRegistros}
					setOpen={setDialogRegistros}
					idEvento={evento}
				/>
			)}
			{invitar && (
				<InvitacionActividad open={invitar} setOpen={setInvitar} data={evento} />
			)}
			<AnadirGastoDialog
				open={gastoOpen}
				onClose={() => setGastoOpen(false)}
				onAdd={handleAdd}
				year={anioSeleccionado}
				cargarDatos={cargarDatos}
				currentApartado={currenApartado}
				evento={evento}
			/>
		</Card>
	);
};
